/**
 * useHomePageData - Custom hook for HomePage data fetching with real server-side pagination
 *
 * Filter persistence contract:
 *   Every filter (search, city, industry, agent, source, funnel stage, target list,
 *   tab, page, next-action) is mirrored to the URL query string. Going to a company
 *   detail and clicking Back restores the same view — even after a hard reload —
 *   because the URL itself carries all state. ``clearAllFilters`` is the ONLY thing
 *   that wipes them.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, getErrorMessage } from "../../../utils/api";
import { FUNNEL_STATUS_MAP } from "../components";

const ITEMS_PER_PAGE = 20;

// Keys persisted in the URL query string. Order is significant only for
// presentation in the URL — the values themselves are read independently.
const URL_KEYS = {
  tab: "tab",
  page: "page",
  q: "q",
  city: "city",
  industry: "industry",
  nextAction: "nextAction",
  agentFilter: "agentFilter",
  source: "source",
  funnelStage: "funnelStage",
  targetListId: "targetListId",
};

export const useHomePageData = (token, user) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Helpers so we can read/write URL params without re-deriving keys each time.
  const readParam = (key) => searchParams.get(key) || "";

  // Hydrate state directly from the URL on every render (URL is the single
  // source of truth). React Router's setSearchParams triggers a re-render so
  // these stay in sync automatically.
  const urlTab = readParam(URL_KEYS.tab) || "active";
  const urlPage = Number(readParam(URL_KEYS.page)) || 1;
  const urlSearch = readParam(URL_KEYS.q);
  const urlCity = readParam(URL_KEYS.city);
  const urlIndustry = readParam(URL_KEYS.industry);
  const urlNextAction = readParam(URL_KEYS.nextAction);
  const urlAgent = readParam(URL_KEYS.agentFilter);
  const urlSource = readParam(URL_KEYS.source);
  const urlFunnelStage = readParam(URL_KEYS.funnelStage) || null;
  const urlTargetList = readParam(URL_KEYS.targetListId);

  // Centralised setter that mutates the URL query string.
  // ``replace: true`` so users do NOT accumulate one history entry per filter
  // change. The single back-button still goes back to whatever route was
  // before "Dnevni zadaci".
  const updateUrl = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "" || v === false) {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Companies - now stores only current page items
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Server-side pagination state (controlled by URL)
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [tabCounts, setTabCounts] = useState({
    all: 0, active: 0, new: 0, follow_up: 0, meeting: 0, closed: 0
  });
  
  // Stats
  const [stats, setStats] = useState({ calls: 0, meetings: 0, acquisitions: 0, offers: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Scores
  const [scores, setScores] = useState({ today: 0, month: 0 });
  const [scoresLoading, setScoresLoading] = useState(true);
  
  // Top Agents
  const [topAgents, setTopAgents] = useState([]);
  const [topAgentsLoading, setTopAgentsLoading] = useState(true);
  
  // Funnel
  const [funnelData, setFunnelData] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);

  // Debounced search — debounce input typing so URL doesn't churn on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);

  // Filter values backed by URL params (read-only views; mutators go through updateUrl)
  const activeTab = urlTab;
  const currentPage = urlPage;
  const searchQuery = urlSearch;
  const cityFilter = urlCity;
  const industryFilter = urlIndustry;
  const nextActionFilter = urlNextAction;
  const agentFilter = urlAgent;
  const sourceFilter = urlSource;
  const funnelStatusFilter = urlFunnelStage;
  const targetListId = urlTargetList;

  // Public setters proxy to URL
  const setActiveTab = (v) => updateUrl({ [URL_KEYS.tab]: v === "active" ? "" : v, [URL_KEYS.page]: "" });
  const setCurrentPage = (v) => updateUrl({ [URL_KEYS.page]: v && v !== 1 ? v : "" });
  const setSearchQuery = (v) => updateUrl({ [URL_KEYS.q]: v, [URL_KEYS.page]: "" });
  const setCityFilter = (v) => updateUrl({ [URL_KEYS.city]: v, [URL_KEYS.page]: "" });
  const setIndustryFilter = (v) => updateUrl({ [URL_KEYS.industry]: v, [URL_KEYS.page]: "" });
  const setNextActionFilter = (v) => updateUrl({ [URL_KEYS.nextAction]: v, [URL_KEYS.page]: "" });
  const setAgentFilter = (v) => updateUrl({ [URL_KEYS.agentFilter]: v, [URL_KEYS.page]: "" });
  const setSourceFilter = (v) => updateUrl({ [URL_KEYS.source]: v, [URL_KEYS.page]: "" });
  const setTargetListId = (v) => updateUrl({ [URL_KEYS.targetListId]: v, [URL_KEYS.page]: "" });
  const setFunnelStatusFilter = (v) => updateUrl({ [URL_KEYS.funnelStage]: v || "", [URL_KEYS.page]: "" });

  // Filter dropdowns data
  const [agents, setAgents] = useState([]);
  const [sources, setSources] = useState([]);
  const [targetLists, setTargetLists] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Auto-expand the filter panel when filters are restored from URL on first paint.
  const filtersAutoExpandedRef = useRef(false);
  useEffect(() => {
    if (filtersAutoExpandedRef.current) return;
    if (cityFilter || industryFilter || nextActionFilter || agentFilter ||
        sourceFilter || targetListId || funnelStatusFilter) {
      setShowFilters(true);
      filtersAutoExpandedRef.current = true;
    }
  }, [cityFilter, industryFilter, nextActionFilter, agentFilter,
      sourceFilter, targetListId, funnelStatusFilter]);

  // All companies cache for filter dropdowns
  const [allCompaniesCache, setAllCompaniesCache] = useState([]);
  
  // Race-condition guard: only the latest fetchCompanies response should win
  const latestFetchIdRef = useRef(0);
  
  // Role checks
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const isTeamLeader = user?.role === 'team_leader';
  const canFilterByAgent = isManagerOrAdmin || isTeamLeader;
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Unique filter values from cache
  const uniqueCities = useMemo(() => {
    const cities = [...new Set(allCompaniesCache.map(c => c.city).filter(Boolean))];
    return cities.sort((a, b) => a.localeCompare(b, 'hr'));
  }, [allCompaniesCache]);

  const uniqueIndustries = useMemo(() => {
    const industries = [...new Set(allCompaniesCache.map(c => c.industry).filter(Boolean))];
    return industries.sort((a, b) => a.localeCompare(b, 'hr'));
  }, [allCompaniesCache]);
  
  // Fetch agents for filter
  useEffect(() => {
    const fetchAgents = async () => {
      if (canFilterByAgent && token) {
        try {
          const response = await apiClient.get('/users/agents');
          setAgents(response.data);
        } catch (error) {
          console.error("Failed to fetch agents:", error);
        }
      }
    };
    fetchAgents();
  }, [token, canFilterByAgent]);

  // Fetch Sources for filter dropdown
  useEffect(() => {
    const fetchSources = async () => {
      if (token) {
        try {
          const response = await apiClient.get('/companies/sources');
          // Backend now returns [{ name, count }]; keep backward compat with old [string]
          const raw = response?.data?.sources || [];
          const normalised = raw.map(s => (
            typeof s === 'string' ? { name: s, count: null } : { name: s.name, count: s.count }
          ));
          setSources(normalised);
        } catch { /* ignore */ }
      }
    };
    fetchSources();
  }, [token]);

  // Fetch Target Lists for filter dropdown
  useEffect(() => {
    if (!token) return;
    const p = apiClient.get('/target-lists');
    if (p && typeof p.then === 'function') {
      p.then(res => setTargetLists(res?.data || []))
        .catch(() => setTargetLists([]));
    }
  }, [token]);

  // Fetch Stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await apiClient.get('/stats/today?range=month');
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch Scores
  const fetchScores = async () => {
    setScoresLoading(true);
    try {
      const [todayRes, monthRes] = await Promise.all([
        apiClient.get('/scores/summary?range=today'),
        apiClient.get('/scores/summary?range=month')
      ]);
      setScores({ today: todayRes.data.points, month: monthRes.data.points });
    } catch (error) {
      console.error("Failed to fetch scores:", error);
    } finally {
      setScoresLoading(false);
    }
  };

  // Fetch Funnel Data
  const fetchFunnelData = async (filterAgentId = null) => {
    setFunnelLoading(true);
    try {
      const params = {};
      if (filterAgentId) params.agent_id = filterAgentId;
      const response = await apiClient.get('/companies/funnel/stats', { params });
      setFunnelData(response.data);
    } catch (error) {
      console.error("Failed to fetch funnel data:", error);
    } finally {
      setFunnelLoading(false);
    }
  };

  // Fetch Top Agents
  const fetchTopAgents = async () => {
    setTopAgentsLoading(true);
    try {
      const response = await apiClient.get('/leaderboard/month?limit=3');
      setTopAgents(response.data.top || []);
    } catch (error) {
      console.error("Failed to fetch top agents:", error);
    } finally {
      setTopAgentsLoading(false);
    }
  };

  // Fetch Companies with server-side pagination
  const fetchCompanies = useCallback(async (tab, page = 1, agentId = null, search = "", statusFilter = null, sourceFilter = null, targetListIdParam = null, retryCount = 0) => {
    const requestId = ++latestFetchIdRef.current;
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({ 
        tab,
        page: page.toString(),
        page_size: ITEMS_PER_PAGE.toString()
      });
      
      // Pass agent filter to backend
      if (agentId && canFilterByAgent) {
        params.append('assigned_to', agentId);
      }
      
      // Pass search to backend
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      // Pass status filter to backend (for funnel clicks)
      if (statusFilter) {
        params.append('status_filter', statusFilter);
      }
      
      // Pass source filter to backend
      if (sourceFilter) {
        params.append('source_filter', sourceFilter);
      }

      // Pass target list id to backend
      if (targetListIdParam) {
        params.append('target_list_id', targetListIdParam);
      }
      
      const response = await apiClient.get(`/companies/queue?${params.toString()}`);

      // Drop stale response if a newer request has been issued in the meantime
      if (requestId !== latestFetchIdRef.current) {
        return;
      }
      
      // Handle both old (array) and new (paginated object) response formats
      // This ensures backward compatibility during deployment transition
      let items, pagination, tab_counts;
      
      if (Array.isArray(response.data)) {
        // OLD FORMAT: response.data is array directly
        items = response.data;
        pagination = {
          page: 1,
          page_size: items.length,
          total: items.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        };
        tab_counts = {
          all: items.length,
          active: items.length,
          new: 0,
          follow_up: 0,
          meeting: 0,
          closed: 0
        };
      } else {
        // NEW FORMAT: response.data is { items, pagination, tab_counts }
        items = response.data.items || [];
        pagination = response.data.pagination || { page: 1, page_size: 20, total: items.length, total_pages: 1, has_next: false, has_prev: false };
        tab_counts = response.data.tab_counts || { all: 0, active: 0, new: 0, follow_up: 0, meeting: 0, closed: 0 };
      }
      
      setCompanies(items);
      // NOTE: we deliberately do NOT call setCurrentPage(pagination.page) here.
      // The URL is the source of truth for `page`; writing it back from a fetch
      // response races with concurrent user actions (filter changes, agent
      // selection) under React Router 7's setSearchParams batching, causing
      // filters to "flash on then reset" on slow networks. Local pagination
      // state below (totalCount/totalPages) is enough for rendering.
      setTotalCount(pagination.total);
      setTotalPages(pagination.total_pages);
      setTabCounts(tab_counts);
      
      // Cache first page for filter dropdowns (only on initial load)
      if (page === 1 && !search && !agentId) {
        setAllCompaniesCache(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newItems = items.filter(c => !existingIds.has(c.id));
          return [...prev, ...newItems];
        });
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
      if (requestId !== latestFetchIdRef.current) return;
      if (retryCount < 1 && (error.code === 'ECONNABORTED' || error.message?.includes('Network'))) {
        setTimeout(() => fetchCompanies(tab, page, agentId, search, statusFilter, sourceFilter, targetListIdParam, retryCount + 1), 1000);
        return;
      }
      toast.error(`Greška pri učitavanju firmi: ${getErrorMessage(error)}`);
    } finally {
      if (requestId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [canFilterByAgent]);

  // Handle funnel status click - now uses server-side filtering via URL
  const handleFunnelStatusClick = (status) => {
    if (funnelStatusFilter === status) {
      setFunnelStatusFilter(null);
    } else {
      setFunnelStatusFilter(status);
      // Switch to 'all' tab when filtering by stages that may live outside the active queue
      if (status === 'nije_zainteresiran' || status === 'ne_kontaktirati' || status === 'akvizicija') {
        updateUrl({ [URL_KEYS.tab]: "all", [URL_KEYS.funnelStage]: status, [URL_KEYS.page]: "" });
      }
    }
  };

  // Convert funnel status to backend status_filter parameter
  const getStatusFilterParam = (funnelStatus) => {
    if (!funnelStatus) return null;
    const statusMap = FUNNEL_STATUS_MAP[funnelStatus];
    if (Array.isArray(statusMap)) {
      return statusMap.join(',');
    }
    return statusMap;
  };

  // Initial data fetch (stats only - fetchCompanies is handled by filter-change effect below)
  useEffect(() => {
    if (token) {
      fetchStats();
      fetchScores();
      fetchTopAgents();
      fetchFunnelData(agentFilter || null);
    }
  }, [token]);

  // Refetch whenever ANY URL-driven filter changes. Reading currentPage from
  // URL means pressing back/forward also re-fetches the correct page without
  // resetting to page 1.
  useEffect(() => {
    if (token) {
      const statusParam = getStatusFilterParam(funnelStatusFilter);
      fetchCompanies(
        activeTab,
        currentPage,
        agentFilter || null,
        debouncedSearch,
        statusParam,
        sourceFilter || null,
        targetListId || null,
      );
      fetchFunnelData(agentFilter || null);
    }
  }, [activeTab, currentPage, agentFilter, debouncedSearch, funnelStatusFilter,
      sourceFilter, targetListId, token, fetchCompanies]);

  // Filter companies locally for city/industry/nextAction (client-side filters)
  // Note: funnelStatusFilter is now handled server-side, so we skip it here
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      // Dropdown filters (client-side only)
      if (cityFilter && company.city !== cityFilter) return false;
      if (industryFilter && company.industry !== industryFilter) return false;
      
      // Next action filter
      if (nextActionFilter) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextActionDateStr = company.last_activity?.next_action_date;
        let nextAction = null;
        if (nextActionDateStr) {
          const dateStr = nextActionDateStr.includes('T') ? nextActionDateStr.split('T')[0] : nextActionDateStr;
          nextAction = new Date(dateStr + 'T00:00:00');
        }
        if (nextActionFilter === "today" && (!nextAction || nextAction.getTime() !== today.getTime())) return false;
        if (nextActionFilter === "overdue" && (!nextAction || nextAction >= today)) return false;
        if (nextActionFilter === "this_week") {
          if (!nextAction) return false;
          const weekEnd = new Date(today);
          weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay()));
          if (nextAction < today || nextAction > weekEnd) return false;
        }
        if (nextActionFilter === "no_date" && nextAction) return false;
      }
      
      return true;
    });
  }, [companies, cityFilter, industryFilter, nextActionFilter, funnelStatusFilter]);

  // Filter state helpers
  const hasActiveFilters = cityFilter || industryFilter || nextActionFilter || agentFilter || funnelStatusFilter || sourceFilter || targetListId || searchQuery;
  const activeFilterCount = [cityFilter, industryFilter, nextActionFilter, agentFilter, funnelStatusFilter, sourceFilter, targetListId, searchQuery].filter(Boolean).length;
  
  const clearAllFilters = () => {
    // Reset filters AND pagination. We clear all our URL keys at once.
    updateUrl({
      [URL_KEYS.q]: "",
      [URL_KEYS.city]: "",
      [URL_KEYS.industry]: "",
      [URL_KEYS.nextAction]: "",
      [URL_KEYS.agentFilter]: "",
      [URL_KEYS.source]: "",
      [URL_KEYS.funnelStage]: "",
      [URL_KEYS.targetListId]: "",
      [URL_KEYS.page]: "",
    });
    // Scroll memory cleanup: dropping all filters also drops any restoration intent
    try { sessionStorage.removeItem("dnevni_zadaci_scroll"); } catch { /* ignore */ }
  };
  
  // Pagination handlers
  const hasMore = currentPage < totalPages;
  const remainingCount = totalCount - (currentPage * ITEMS_PER_PAGE);
  
  const handleLoadMore = useCallback(() => {
    const nextPage = currentPage + 1;
    setLoading(true);
    
    // Fetch next page and append to existing items
    const params = new URLSearchParams({ 
      tab: activeTab,
      page: nextPage.toString(),
      page_size: ITEMS_PER_PAGE.toString()
    });
    
    if (agentFilter && canFilterByAgent) {
      params.append('assigned_to', agentFilter);
    }
    if (debouncedSearch.trim()) {
      params.append('search', debouncedSearch.trim());
    }
    // Add status filter if active
    const statusParam = getStatusFilterParam(funnelStatusFilter);
    if (statusParam) {
      params.append('status_filter', statusParam);
    }
    // Add source filter if active
    if (sourceFilter) {
      params.append('source_filter', sourceFilter);
    }
    // Add target list filter if active
    if (targetListId) {
      params.append('target_list_id', targetListId);
    }
    
    apiClient.get(`/companies/queue?${params.toString()}`)
      .then(response => {
        // Handle both old (array) and new (paginated object) response formats
        let items, pagination;
        
        if (Array.isArray(response.data)) {
          // OLD FORMAT: response.data is array directly
          items = response.data;
          pagination = { page: nextPage, total_pages: 1 };
        } else {
          // NEW FORMAT: response.data is { items, pagination, tab_counts }
          items = response.data.items || [];
          pagination = response.data.pagination || { page: nextPage, total_pages: 1 };
        }
        
        setCompanies(prev => [...prev, ...items]);
        setCurrentPage(pagination.page);
        setTotalPages(pagination.total_pages);
      })
      .catch(error => {
        console.error("Failed to load more companies:", error);
        toast.error(`Greška pri učitavanju: ${getErrorMessage(error)}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentPage, activeTab, agentFilter, debouncedSearch, funnelStatusFilter, sourceFilter, targetListId, canFilterByAgent]);
  
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // The fetch effect picks up the new currentPage via the URL.
    }
  }, [totalPages, setCurrentPage]);

  return {
    // Companies
    companies,
    filteredCompanies,
    displayedCompanies: filteredCompanies, // For backward compatibility
    loading,
    activeTab,
    setActiveTab,
    
    // Stats
    stats,
    statsLoading,
    
    // Scores
    scores,
    scoresLoading,
    
    // Top Agents
    topAgents,
    topAgentsLoading,
    
    // Funnel
    funnelData,
    funnelLoading,
    funnelStatusFilter,
    handleFunnelStatusClick,
    
    // Server-side Pagination
    currentPage,
    totalCount,
    totalPages,
    tabCounts,
    hasMore,
    remainingCount: Math.max(0, remainingCount),
    handleLoadMore,
    goToPage,
    
    // Filters
    searchQuery,
    setSearchQuery,
    cityFilter,
    setCityFilter,
    industryFilter,
    setIndustryFilter,
    nextActionFilter,
    setNextActionFilter,
    agentFilter,
    setAgentFilter,
    agents,
    sourceFilter,
    setSourceFilter,
    sources,
    targetListId,
    setTargetListId,
    targetLists,
    showFilters,
    setShowFilters,
    uniqueCities,
    uniqueIndustries,
    hasActiveFilters,
    activeFilterCount,
    clearAllFilters,
    resetDisplayCount: () => {}, // No-op for backward compatibility
    
    // Role helpers
    isManagerOrAdmin,
    isTeamLeader,
    canFilterByAgent
  };
};
